import {Flex, HStack, Stack, Text} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React from 'react'
import {useIntl} from 'react-intl'
import useOcapiSite from '../../../hooks/useOcapiSite'
import {Box2OutlineIcon} from '../../../components/icons'
import {ContentAssetModal, useContentAssetModal} from '../../../hooks/use-content-asset-modal'
import AddToCartCTA from '../../../components/add-to-cart-cta'
import ProductAvailability from '../../../leap/components/product-availability/index.jsx'
import ProductDisplayPrice from '../../../components/product-display-price'
import PayLater from '../../../components/pay-later'
import useProductPricing from '../../../hooks/use-product-pricing'
import StyledContent from '../../../components/styled-content'
import QuantityPicker from '../../../components/quantity-picker'
import {useCroVariantActive} from '../../../components/cro'
import {CRO_EXPERIMENTS} from '../../../constants'
import AddToBabylist from '../../../components/add-to-babylist'
import {useCommerceData} from '../../../hooks/use-commerce-data'

const CockpitOrderSection = ({
    derivedProduct,
    isGetPropsLoading,
    isProductASet,
    isPromotionsLoading,
    isVariantLoading,
    product,
    onAddToCartClick,
    onRecommendedProductClick
}) => {
    const {shippingContentAssetId} = useOcapiSite()
    const contentModal = useContentAssetModal(shippingContentAssetId)
    const {formatMessage} = useIntl()
    const {currency, displayPrice} = useProductPricing(product) || {}
    const {isEcommerceEnabled, showPricesOnContentOnly} = useCommerceData(product)
    const {
        isOrderable,
        minOrderQuantity,
        maxOrderQuantity,
        quantity,
        stepQuantity,
        onChangeQuantity
    } = derivedProduct

    const cro65Active = useCroVariantActive(
        CRO_EXPERIMENTS.cro65PdpHideDeliveryTime.experimentId,
        CRO_EXPERIMENTS.cro65PdpHideDeliveryTime.variantId
    )

    return (
        <Stack spacing={4}>
            {(isEcommerceEnabled || showPricesOnContentOnly) && !isProductASet && (
                <Stack spacing={0}>
                    <Flex gap={4.5} alignItems="flex-end" justifyContent="space-between">
                        {isEcommerceEnabled ? (
                            <ProductAvailability
                                loading={isVariantLoading}
                                flexShrink="0"
                                product={product}
                            />
                        ) : (
                            <>&nbsp;</>
                        )}
                        <ProductDisplayPrice
                            product={product}
                            loading={isGetPropsLoading || isPromotionsLoading || isVariantLoading}
                            variant="pdp"
                        />
                    </Flex>
                </Stack>
            )}
            {!isProductASet && (
                <>
                    <PayLater
                        amount={displayPrice}
                        currency={currency}
                        paypalPlacement="product"
                        klarnaPlacement="credit-promotion-badge"
                    />
                    <Stack spacing={3}>
                        {isOrderable && isEcommerceEnabled && (
                            <QuantityPicker
                                containerProps={{
                                    // matches the button style
                                    maxWidth: {md: '25rem'}
                                }}
                                btnWidth={15}
                                height={14}
                                minQuantity={minOrderQuantity}
                                maxQuantity={maxOrderQuantity}
                                quantity={quantity}
                                quantityStep={stepQuantity}
                                onChange={onChangeQuantity}
                            />
                        )}
                        <AddToCartCTA
                            isLoading={isGetPropsLoading || isPromotionsLoading || isVariantLoading}
                            product={product}
                            quantity={quantity}
                            onClick={onAddToCartClick}
                            onRecommendedProductClick={onRecommendedProductClick}
                        />
                    </Stack>
                </>
            )}
            <Stack spacing={4}>
                {!cro65Active && product?.c_estimatedDelivery && (
                    <HStack spacing={2} alignItems="center">
                        <Box2OutlineIcon boxSize={4} />
                        <Text textStyle="Regular Small">
                            {formatMessage(
                                {
                                    id: 'checkout.shipping.delivery_time',
                                    defaultMessage: 'Delivery from: {time}',
                                    description:
                                        'The date range when delivery is expected to happen. {time} contains range like "24.11.2021 - 26.11.2021"'
                                },
                                {time: product.c_estimatedDelivery}
                            )}
                        </Text>
                    </HStack>
                )}

                <Stack spacing={4} paddingTop={2} alignItems="flex-start">
                    <AddToBabylist product={product} />
                    {product.c_pdpCockpitUspContent && (
                        <StyledContent content={product.c_pdpCockpitUspContent} />
                    )}
                </Stack>
            </Stack>
            <ContentAssetModal {...contentModal} variant="pdp" />
        </Stack>
    )
}

CockpitOrderSection.propTypes = {
    derivedProduct: PropTypes.object,
    isGetPropsLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    isProductASet: PropTypes.bool,
    product: PropTypes.object,
    onAddToCartClick: PropTypes.func,
    onRecommendedProductClick: PropTypes.func
}

export default CockpitOrderSection
